import { Injectable } from '@angular/core';
import { UnitsService,FormattingService} from 'pumpcalc-core';
import { Keys, Labels, LabelToKeys, PipeDimensionKeys } from '../gasflow.constants';
import { RangeVariable } from '../range-variable-form/range-variable-form.component';

export class Plot {
  arg: string;
  name: string;
  val: string;
  logX:boolean;
}

export class SeriesSelection {
  series: any[];
  plots: Plot[];
}

@Injectable({
  providedIn: 'root'
})
//Used in conjunction with dx-accordion
export class ResultTableService {

  constructor(public unitsService: UnitsService, public formattingService: FormattingService) {}

  initSelectedHeaders(keys,resultMeta,selectedHeaders) {
    keys.forEach(key => {
      if (resultMeta[key].isHeader) {selectedHeaders[key] = false };
    });
  }

  setVisibleKeysForRangedResult(keys,visibleKeys,resultMeta,rangedVariableKeys) {
    keys.forEach(key => {
      visibleKeys[key] = resultMeta[key].isHeader ? true : false;
    })
    visibleKeys[rangedVariableKeys[0]] = true;
  }

  isMultiPipeResult(solverResultsAndConditonsAndRangeVariable) {
    let isMultiPipeReport = false;
    solverResultsAndConditonsAndRangeVariable.forEach(solverResultsAndConditon => {
      if (solverResultsAndConditon.solverResult.results[0].pipeReports.length > 1) {
        isMultiPipeReport = true; return;
      }
    });
    return isMultiPipeReport;
  }

  selectionStateAfterToggleKeySelection(key,selectedKeys,isRangedResult,resultMeta,forceTrue?:boolean):{isSomethingSelected:boolean,selectedKeys:any} {
    let somethingSelected = false;
    let newSelectedKeys = [];
    let deSelect = selectedKeys[key];
    if (!deSelect || forceTrue) { newSelectedKeys[key] = true; somethingSelected = true; }
    Object.keys(selectedKeys).forEach(alreadySelectedKey => {
      if ((!deSelect || (deSelect && alreadySelectedKey != key)) &&
        (!isRangedResult || this.valuesHaveSameQuantityOrKey(alreadySelectedKey, key,resultMeta))) {
        newSelectedKeys[alreadySelectedKey] = true;
        somethingSelected = true;
      }
    })
    selectedKeys = newSelectedKeys;
    return {isSomethingSelected:somethingSelected,selectedKeys:selectedKeys};
  }

  valuesHaveSameQuantityOrKey(key1,key2,resultMeta): boolean {
    let quantitiesDefinedForBoth = this.isDefined(resultMeta[key1].quantity) && this.isDefined(resultMeta[key2].quantity);
    let keysMatch = resultMeta[key1].key == resultMeta[key2].key;
    let quantitiesMatch = resultMeta[key1].quantity == resultMeta[key2].quantity;
    return (quantitiesDefinedForBoth && quantitiesMatch) || (quantitiesMatch && keysMatch)
  }

  isDefined(variable): boolean {
    return typeof (variable) != "undefined"
  }

  setReferenceVariableAsVisible(visibleKeys,rangedVariableKeys) {
    visibleKeys[Object.keys(rangedVariableKeys)[0]] = true;
  }

  selectAndMakeVisibleHeaderAndReferenceVariable(heading,selectedHeaders,resultMeta,visibleKeys,rangedVariableKeys,forceSelect?:boolean){
    selectedHeaders[heading] = forceSelect ? true : !selectedHeaders[heading];
    Object.keys(resultMeta).forEach(valueKey => {
      visibleKeys[valueKey] = selectedHeaders[resultMeta[valueKey].heading];
    });
    this.setReferenceVariableAsVisible(visibleKeys,rangedVariableKeys);
  }

  setSelectedKeysAndHeadersAsVisible(visibleKeys,selectedKeys,selectedHeaders) {
    Object.keys(visibleKeys).forEach(key => {
      visibleKeys[key] = selectedKeys[key]
    })
    Object.keys(selectedHeaders).forEach(key => {
      visibleKeys[key] = true;
      selectedHeaders[key] = false;
    })
  }

  getSelectedSeries(solverResultsAndConditonsAndRangeVariable,rangedVariableKeys,selectedKeys,resultMeta): SeriesSelection {
    let rangeVariableKey = Object.keys(rangedVariableKeys)[0];
    let rangedVariableArg = this.getKeyForPlot(rangeVariableKey,resultMeta);
    let valQuantity = Object.keys(selectedKeys).length > 0 ? resultMeta[Object.keys(selectedKeys)[0]].quantity : null;
    let valTitle = this.isDefined(valQuantity) ? valQuantity : Object.keys(selectedKeys).length > 0 ? Labels[resultMeta[Object.keys(selectedKeys)[0]].key] : "?"
    let plots = [];
    Object.keys(selectedKeys).forEach(key => {
      plots.push({
        arg: rangedVariableArg,
        argQuantity: resultMeta[rangeVariableKey].quantity,
        argTitle: rangeVariableKey,
        val: this.getKeyForPlot(key,resultMeta),
        valQuantity: valQuantity,
        valTitle: valTitle,
        name: key,
        logX: solverResultsAndConditonsAndRangeVariable[0].rangeVariable[1].log
      })
    })
    return { series: solverResultsAndConditonsAndRangeVariable[0].solverResult.results, plots: plots };
  }

  getKeyForPlot(key,resultMeta): string {
    let keyForPlot = LabelToKeys[key] ? LabelToKeys[key] : resultMeta[key].pipeIndex >= 0 ? resultMeta[key].pipeIndex + resultMeta[key].key : undefined;
    return keyForPlot;
  }

  getKeysAndResultMeta(results: any[]):{keys:any,resultMeta:any} {
    let longestResult = {};
    results.forEach(result => {
      longestResult = Object.keys(result).length > Object.keys(longestResult).length ? result : longestResult;
    })
    return {keys:Object.keys(longestResult),resultMeta:longestResult}
  }

  getLabelFromRangeVariable(rangeVariable: RangeVariable): string {
    return rangeVariable.pipe >= 0 ? "Pipe " + rangeVariable.pipe + " " + Labels[PipeDimensionKeys[rangeVariable.key]] : Labels[rangeVariable.key];
  }

  setAllAsVisible(keys,visibleKeys,selectedHeaders) {
    keys.forEach(key => {
      visibleKeys[key] = true;
    })
    Object.keys(selectedHeaders).forEach(key => {
      selectedHeaders[key] = true;
    })
  }

  getResultsAsText(visibleKeys,results) {
    let resultText = "";
    Object.keys(visibleKeys).forEach(key => {
      if (key != "undefined" && key != undefined && visibleKeys[key] == true && results[0][key] && !results[0][key].isHeading) {
        let anyUnit = (results[0][key] && results[0][key].quantity) ? " (" + this.unitsService.getSelectedUnitForQuantity(results[0][key].quantity) + ")" : "";
        resultText += this.spacePaddingRight(key + anyUnit, 40);
        results.forEach(result => {
          let value = result[key] ? (result[key].quantity ? this.formattingService.formatNumber(this.unitsService.convertFromSIForQuantity(result[key].quantity, result[key].value)) : (this.formattingService.isNumber(result[key].value) ? this.formattingService.formatNumber(result[key].value) : result[key].value)) : "";
          resultText += "\t" + this.spacePaddingRight(value, 10);
        })
        resultText += "\r\n";
      }
    });
    return resultText;
  }

  spacePaddingRight(value, length) {
    return String(value + "                                                  " + this).slice(0, length);
  };

  cloneSelection(selection):any{
    let result=[];
    Object.keys(selection).forEach(key=>{
      result[key]=selection[key];
    })
    return result;
  }

}
